import { useMemo } from 'react';
import type { BlindKingsGame, GamePiece, RawPiece } from '@blind-kings/game-core';
import { PIECE_VALUES } from '@blind-kings/game-core';
import { resolveTeamPalettes } from '../../player-colors';
import PieceInfo from './PieceInfo';
import { BoardCanvas } from './pixi';
import type { PlayRequest, BoardFocus } from './hooks/use-optimistic-game-actions';

export type FullGameBoardProps = {
  blindKingsGame: BlindKingsGame;
  canvasBoard?: RawPiece[][];
  selectPiece?: (piece: GamePiece) => void;
  selectedPiece?: GamePiece;
  displayPieceInfo?: boolean;
  displayPieceValues?: boolean;
  renderVertically?: boolean;
  skinId?: string;
  pendingFlipPieceId?: number;
  localAction?: PlayRequest | null;
  remoteFocus?: BoardFocus;
  onPlayComplete?: (actionId: string) => void;
  inputLocked?: boolean;
};

/** Rail thickness as a fraction of the board+rails unit (scales with board). */
const RAIL_FR = 0.09;

export default function FullGameBoard({
  blindKingsGame,
  canvasBoard,
  selectPiece,
  selectedPiece,
  displayPieceInfo,
  displayPieceValues = true,
  renderVertically,
  skinId = 'classic',
  pendingFlipPieceId,
  localAction = null,
  remoteFocus,
  onPlayComplete,
  inputLocked = false,
}: FullGameBoardProps) {
  const {
    gameBoard,
    firstPlayer,
    secondPlayer,
    isGameOver,
    isUserTurn,
    turnIndex,
  } = blindKingsGame;
  const board = canvasBoard ?? blindKingsGame.board ?? [];
  const actionablePieces = isGameOver ? [] : (selectedPiece?.allAvailablePiecesForAction ?? []);
  const invalidPieces = isGameOver ? [] : (selectedPiece?.allInvalidMovePieces ?? []);

  const teamPalettes = useMemo(
    () =>
      resolveTeamPalettes(
        firstPlayer?.colorId as string | undefined,
        secondPlayer?.colorId as string | undefined
      ),
    [firstPlayer?.colorId, secondPlayer?.colorId]
  );

  const resolvedSkinId =
    skinId !== 'classic'
      ? skinId
      : teamPalettes[0]?.spritePackId ?? teamPalettes[1]?.spritePackId ?? 'classic';

  // Pixi pointer infrastructure stays live for the whole match. Turn ownership is
  // enforced by selectPiece (game state) and hoverablePieceIds — not by remounting
  // or flipping canvas/stage interactivity, which only apply their initial value.
  const boardInteractive = !isGameOver;
  const hoverEnabled = boardInteractive && !!isUserTurn && !inputLocked;

  const hoverablePieceIds = useMemo(() => {
    if (!hoverEnabled) return new Set<number>();
    const ids = new Set<number>();
    const pieces = Object.values(gameBoard?.gamePiecesById ?? {});
    if (selectedPiece) {
      ids.add(selectedPiece.id);
      for (const p of actionablePieces) ids.add(p.id);
      for (const p of invalidPieces) ids.add(p.id);
      for (const p of pieces) {
        if (p.teamIndex === turnIndex) ids.add(p.id);
        if (p.isUnturned || p.value === PIECE_VALUES.Unturned) ids.add(p.id);
      }
      return ids;
    }
    for (const p of pieces) {
      if (p.value === PIECE_VALUES.Empty) continue;
      if (
        p.isUnturned ||
        p.value === PIECE_VALUES.Unturned ||
        p.teamIndex === turnIndex
      ) {
        ids.add(p.id);
      }
    }
    return ids;
  }, [
    hoverEnabled,
    selectedPiece,
    actionablePieces,
    invalidPieces,
    gameBoard,
    turnIndex,
  ]);

  const boardCanvas = (
    <BoardCanvas
      board={board}
      gameBoard={gameBoard}
      renderVertically={renderVertically}
      selectedPiece={isGameOver ? undefined : selectedPiece}
      displayPieceValues={displayPieceValues}
      actionablePieces={actionablePieces}
      invalidPieces={invalidPieces}
      pendingFlipPieceId={pendingFlipPieceId}
      skinId={resolvedSkinId}
      interactive={boardInteractive}
      teamPalettes={teamPalettes}
      playRequest={localAction}
      remoteFocus={remoteFocus}
      onPlayComplete={onPlayComplete}
      selectPiece={boardInteractive ? selectPiece : undefined}
      gamePiecesById={gameBoard.gamePiecesById}
      hoverablePieceIds={hoverablePieceIds}
    />
  );

  if (!displayPieceInfo) {
    return (
      <div className="flex h-full w-full justify-center">
        <div className="min-h-0 min-w-0 flex-1 border-2 border-black">{boardCanvas}</div>
      </div>
    );
  }

  const rail0 = (
    <PieceInfo
      renderVertically={renderVertically}
      playerIndex={0}
      blindKingsGame={blindKingsGame}
      teamPalettes={teamPalettes}
    />
  );
  const rail1 = (
    <PieceInfo
      renderVertically={renderVertically}
      playerIndex={1}
      blindKingsGame={blindKingsGame}
      teamPalettes={teamPalettes}
    />
  );

  const boardStack = (
    <div
      className="grid min-h-0 min-w-0 flex-1 border-2 border-black"
      style={
        renderVertically
          ? {
              gridTemplateColumns: `${RAIL_FR}fr 1fr ${RAIL_FR}fr`,
              gridTemplateRows: 'minmax(0, 1fr)',
            }
          : {
              gridTemplateRows: `${RAIL_FR}fr 1fr ${RAIL_FR}fr`,
              gridTemplateColumns: 'minmax(0, 1fr)',
            }
      }
    >
      {rail0}
      <div className="min-h-0 min-w-0">{boardCanvas}</div>
      {rail1}
    </div>
  );

  return (
    <div className="flex h-full w-full justify-center">
      {boardStack}
    </div>
  );
}
